import React, { useState, useRef, useEffect, Dispatch, SetStateAction, useCallback } from 'react'
import styled from 'styled-components'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Dropdown from 'components/Dropdown'
import Search from 'components/Search'
import FilterGroup from 'pages/Customers/CustomerSearch/FilterGroup'
import SelectedFilterList from 'pages/Customers/CustomerSearch/SelectedFilterList'
import GA4 from 'helpers/ga4'
import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

type Props = {
  query?: string;
  filters?: string;
  onSearch: (arg1: string, arg2: string) => void;
  className?: string;
  showSpinner: boolean
}

const CustomerSearch = ({ query = '', filters = '', onSearch, className = '', showSpinner = false }: Props) => {
  const [inputValue, setInputValue] = useState(query)
  const [selectedFilters, setSelectedFilters] = useState((filters || '').split(',').filter(Boolean))
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    searchInputRef.current?.focus()
  }, [searchInputRef])

  const filterGroups = () => {
    const alertArray = ['critical', 'major', 'minor', 'sda_major', 'sda_minor', 'dirty_filter']

    const FILTER_GROUPS = [
      { groupPosition: 'L', label: 'Status', filters: ['opted_in', 'opted_out', 'not_enrolled'] },
      { groupPosition: 'R', label: 'Alerts', filters: alertArray },
      { groupPosition: 'L', label: 'Action', filters: ['resolved', 'scheduled_repair', 'scheduled_maintenance', 'customer_action_required', 'actively_monitoring', 'ordered_parts'] },
      { groupPosition: 'R', label: 'Connection', filters: ['connected', 'disconnected'] },
      { groupPosition: 'L', label: 'Service Contract', filters: ['active', 'expired', 'no_contract'] },
      { groupPosition: 'R', label: 'Pin Status', filters: ['pinned', 'unpinned'] },
      { groupPosition: 'L', label: 'Thermostats', filters: ['724', '824', '850', '1050', 'SC360', 'PIVOT'] },
      { groupPosition: 'R', label: 'Customer Info', filters: ['new', 'updated', 'duplicate'] }
    ]

    return FILTER_GROUPS
  }

  const previouslySelected = (oldFilters, group) => {
    let existingLabelFilter

    oldFilters.forEach((oldFilter) => {
      if (oldFilter !== 'not_enrolled') {
        group.filters.forEach(filter => {
          if (filter === oldFilter) existingLabelFilter = filter
        })
      }
    })

    if (existingLabelFilter !== undefined) {
      oldFilters = oldFilters.filter(oldFilter => oldFilter !== existingLabelFilter)
    }
    return oldFilters
  }

  const applyFiltersCallback = useCallback(() => {
    if (inputValue !== '') GA4.sendEvent({ category: 'Customer List Page', action: 'Execute Search', label: inputValue })
    onSearch(inputValue, selectedFilters.join(','))
  }, [inputValue, selectedFilters, onSearch])

  const applyFiltersAndCloseCallback = useCallback(() => {
    if (selectedFilters.length > 0) {
      GA4.sendEvent({ category: 'Customer List Page', action: 'Click Apply Filter', label: selectedFilters.join(',') })
    }
    onSearch(inputValue, selectedFilters.join(','))
    setIsDropdownOpen(false)
  }, [inputValue, selectedFilters, onSearch])

  const clearFiltersAndCloseCallback = useCallback(() => {
    setSelectedFilters([])
    setIsDropdownOpen(false)
    onSearch(inputValue, '')
  }, [inputValue, onSearch])

  const removeFilterCallback = useCallback((selectedFilter) => {
    const filters = selectedFilters.filter(value => value !== selectedFilter)
    setSelectedFilters(filters)
    onSearch(inputValue, filters.join(','))
  }, [selectedFilters, inputValue, onSearch])

  const clearAllFiltersCallback = useCallback(() => {
    setSelectedFilters([])
    onSearch(inputValue, '')
  }, [inputValue, onSearch])

  const toggleFilterCallback = useCallback((group, filter, isSelected) => {
    if (isSelected) {
      setSelectedFilters((oldFilters) => oldFilters.filter(oldFilter => oldFilter !== filter))
      searchInputRef.current?.focus()
    } else {
      setSelectedFilters((oldFilters) => {
        if (group.label !== 'Action' && group.label !== 'Alerts' && filter !== 'not_enrolled') {
          oldFilters = previouslySelected(oldFilters, group)
        }

        return [...oldFilters, filter]
      })
      searchInputRef.current?.focus()
    }
  }, [])

  const onCloseDropDownCallback = useCallback((event, isClickOutsideDropdown) => {
    if (isClickOutsideDropdown && isDropdownOpen) {
      setSelectedFilters((filters || '').split(',').filter(Boolean))
      onSearch(inputValue, filters)
    }
    setIsDropdownOpen(false)
  }, [isDropdownOpen, inputValue, filters, onSearch])

  return (
    <S.Container className={className}>
      <S.SearchContainer>
        <S.FilterIcon
          className="icon-filter"
          onClick={() => {
            setIsDropdownOpen((oldIsDropdownOpen) => !oldIsDropdownOpen)
            searchInputRef.current?.focus()
          }}
          anyFilterSelected={selectedFilters.length > 0}
          role="button"
          aria-label="filters"
        />

        <S.Search
          ref={searchInputRef}
          ariaLabel="customer search"
          placeholder="Search"
          value={inputValue}
          showClearIcon={inputValue !== ''}
          onClickClearIcon={() => {
            setInputValue('')
            searchInputRef.current?.focus()
            onSearch('', selectedFilters.join(','))
          }}
          onChange={setInputValue}
          onSearch={() => applyFiltersCallback()}
          showSpinner={showSpinner}
        />
      </S.SearchContainer>

      <S.Dropdown isOpen={isDropdownOpen} onClose={onCloseDropDownCallback} ariaLabelsToIgnoreOnOutsideClick={['search', 'filters']}>
        <S.AdvanceFilters>
          <S.LeftGroup>
            {filterGroups().map(group => (
              <div key={group.label}>{group.groupPosition === 'L' && <FilterGroup {...{ group, selectedFilters, toggleFilterCallback }} />}</div>
            ))}
          </S.LeftGroup>
          <S.RightGroup>
            {filterGroups().map(group => (
              <div key={group.label}>{group.groupPosition === 'R' && <FilterGroup {...{ group, selectedFilters, toggleFilterCallback }} />}</div>
            ))}
          </S.RightGroup>
        </S.AdvanceFilters>
        <S.Buttons>
          <Button decoration="secondary" text="Clear" onPress={clearFiltersAndCloseCallback} />
          <Button decoration="positive" text="Apply Filter" onPress={applyFiltersAndCloseCallback} />
        </S.Buttons>
      </S.Dropdown>

      {(selectedFilters.length > 0 && !isDropdownOpen) && <SelectedFilterList {...{ selectedFilters, clearAllFiltersCallback, removeFilterCallback }} />}
    </S.Container>
  )
}

export default CustomerSearch

const S = {
  Container: styled.div`
    white-space: nowrap;
  `,
  SearchContainer: styled.div`
    display: flex;
    flex-wrap: nowrap;
  `,
  FilterIcon: styled.button.withConfig({
    shouldForwardProp: (prop) => !['anyFilterSelected'].includes(prop)
  })<{ anyFilterSelected: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 45px;
    height: 39px;
    font-size: 25px;
    color: ${color('filterIcon')};
    border: ${color('border')} 1px solid;
    cursor: pointer;

    ${({ anyFilterSelected }) => anyFilterSelected && `
      color: ${color('paneBackground')};
      background-color: ${color('filterIcon')};
    `}
  `,
  Search: styled(Search)<{ ariaLabel: string, placeholder: string, value: string, showClearIcon: boolean,
    onClickClearIcon: () => void, onChange: Dispatch<SetStateAction<string>>, onSearch: () => void, showSpinner: boolean, }>`
    display: inline-block;

    input {
      border-radius: 0;
    }
  `,
  Dropdown: styled(Dropdown)`
    max-width: 50%;
    margin-right: 30px;
    background: ${color('paneBackground')};
    border: 2px solid ${color('paneBackground')};
    border-radius: 8px;

    @media ${screenSize.small} or ${screenSize.medium} {
      max-width: 96%;
    }
  `,
  AdvanceFilters: styled.div`
    display: grid;
    grid-template-columns: 60% 40%;

    @media ${screenSize.small} {
      grid-template-columns: 100%;
    }
  `,
  LeftGroup: styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  `,
  RightGroup: styled.div`
    display: flex;
    flex-direction: column;
  `,
  Buttons: styled.div`
    display: flex;
    justify-content: center;
    gap: 20px;
    margin: 15px;
    background-color: ${color('paneBackground')};
  `
}
